import React, { memo, useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  Table,
  Row,
  Col,
} from "reactstrap";
import axios from "axios";
import { roundImportPrice as roundPrice, formatImportPrice, prepareImportSubmission, remainingImportItems, reviewImportItem, selectImportStock, importHeaderMetadata } from "./cameraImportUtils";
import { v4 as uuidv4 } from "uuid";
import { END_POINT } from "../../../constants/defaultValues";
import {
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import Creatable from "../../../components/common/CreatableSelect";
import "../../../assets/css/multi-warehouse-import.css";

const formatNumberWithDot = (value) => typeof value === "string" ? value : formatImportPrice(value);

// Làm tròn số lượng tồn: tối đa 2 chữ số sau dấu chấm
const roundStockCount = (count) => {
  if (count === null || count === undefined || count === "") return 0;
  const num = Number(count);
  if (isNaN(num)) return 0;
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

const emptyItem = () => ({
  id: uuidv4(),
  stock_id: null,
  name: "",
  unit: "cái",
  quantity: 1,
  price: 0,
  retail_price: "",
  paid_amount: undefined,
  is_paid_custom: false,
  provider_name: "",
  note: "",
  expiry_date: "",
  barcode: "",
});

const todayDate = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const formatFallbackReceiptCode = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const year = isNaN(d.getFullYear()) ? new Date().getFullYear() : d.getFullYear();
  const month = isNaN(d.getMonth()) ? String(new Date().getMonth() + 1).padStart(2, "0") : String(d.getMonth() + 1).padStart(2, "0");
  const day = isNaN(d.getDate()) ? String(new Date().getDate()).padStart(2, "0") : String(d.getDate()).padStart(2, "0");
  return `PN${year}${month}${day}01`;
};

const AddWarehouseImport = ({
  title,
  isOpen,
  toggle,
  onSuccess,
  data_stocks = [],
  onOpenScanner,
  incomingScannedItems = null,
  onClearIncomingScannedItems,
}) => {
  const [transactionDate, setTransactionDate] = useState(todayDate);
  const [receiptCode, setReceiptCode] = useState(() => formatFallbackReceiptCode(todayDate()));
  const [items, setItems] = useState([emptyItem()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const requestIdRef = useRef(null);
  const submittingRef = useRef(false);
  const [retryPending, setRetryPending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const editsDisabled = isSubmitting || retryPending;
  const barcodeInputsRef = useRef({});
  const pendingFocusRowIdRef = useRef(null);
  const inputsRef = useRef({});
  const pendingFocusCellRef = useRef(null);
  const dateInputRef = useRef(null);
  const selectRefs = useRef({});

  const isRowBlank = (it) =>
    !String(it.name || "").trim() &&
    !String(it.barcode || "").trim() &&
    !String(it.provider_name || "").trim() &&
    !String(it.note || "").trim() &&
    (!it.price || Number(it.price) === 0) &&
    (it.quantity === 1 || it.quantity === "1" || !it.quantity) &&
    (it.unit === "cái" || !it.unit);

  const selectAllInput = (el) => {
    if (!el) return;
    try {
      if (typeof el.select === "function") {
        el.select();
      }
    } catch (_) {}
    try {
      if (typeof el.setSelectionRange === "function") {
        el.setSelectionRange(0, el.value ? String(el.value).length : 0);
      }
    } catch (_) {}
  };

  const handleInputFocus = (e) => {
    const target = e.target;
    if (target) {
      setTimeout(() => selectAllInput(target), 0);
    }
  };

  const handleInputClick = (e) => {
    const target = e.target;
    if (target) {
      selectAllInput(target);
    }
  };

  const handleKeyDownEnter = (e, index, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const nextIndex = index + 1;
      if (nextIndex < items.length) {
        const nextEl = inputsRef.current[`${nextIndex}-${field}`];
        if (nextEl) {
          nextEl.focus();
          setTimeout(() => selectAllInput(nextEl), 0);
        } else {
          setTimeout(() => {
            const el = inputsRef.current[`${nextIndex}-${field}`];
            if (el) {
              el.focus();
              selectAllInput(el);
            }
          }, 30);
        }
      } else {
        pendingFocusCellRef.current = { index: nextIndex, field };
        handleAddItem();
      }
    }
  };

  const [businessType, setBusinessType] = useState(
    () => localStorage.getItem("businessType") || localStorage.getItem("business_type") || ""
  );



  // Nạp danh sách nhà cung cấp & ngành hàng kinh doanh & mã phiếu tiếp theo
  useEffect(() => {
    if (!isOpen || retryPending) return undefined;
    let active = true;
    setReceiptCode(formatFallbackReceiptCode(transactionDate));
    axios.get(END_POINT + "/warehouse-import/next-code", {
      params: { date: transactionDate },
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
    }).then((res) => {
      if (active && !submittingRef.current && typeof res.data?.data === "string") setReceiptCode(res.data.data);
    }).catch(() => {});
    return () => { active = false; };
  }, [isOpen, transactionDate, retryPending]);

  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem("access_token");

      axios
        .get(`${END_POINT}/company-provider`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data?.data) setProviders(res.data.data);
        })
        .catch(() => {});

      axios
        .get(`${END_POINT}/user/business-type`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data?.data) {
            setBusinessType(res.data.data);
            localStorage.setItem("businessType", res.data.data);
          }
        })
        .catch(() => {});
    } else {
      setHasSubmitted(false);
    }
  }, [isOpen]);

  // Ngành hàng kèm barcode (non-FnB như tạp hóa, bán lẻ, siêu thị...) hoặc có hàng hóa mang barcode
  const showBarcode = useMemo(() => {
    const bt = String(businessType || "").trim().toLowerCase();
    if (bt === "cafe" || bt === "restaurant") {
      return data_stocks.some((s) => s.barcode && String(s.barcode).trim() !== "");
    }
    return true;
  }, [businessType, data_stocks]);

  // Tự động focus vào ô barcode của dòng mới tạo sau khi quét
  useEffect(() => {
    if (pendingFocusRowIdRef.current) {
      const id = pendingFocusRowIdRef.current;
      pendingFocusRowIdRef.current = null;
      const timer = setTimeout(() => {
        const el = barcodeInputsRef.current[id];
        if (el) {
          el.focus();
          selectAllInput(el);
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [items]);

  // Tự động focus và tô đen ô ở hàng mới khi nhấn Enter ở hàng cuối cùng
  useEffect(() => {
    if (pendingFocusCellRef.current) {
      const { index, field } = pendingFocusCellRef.current;
      pendingFocusCellRef.current = null;
      const timer = setTimeout(() => {
        const el = inputsRef.current[`${index}-${field}`];
        if (el) {
          el.focus();
          selectAllInput(el);
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [items]);

  // Tự động focus vào ô barcode đầu tiên khi mở modal
  useEffect(() => {
    if (isOpen && showBarcode && items.length > 0) {
      const timer = setTimeout(() => {
        const firstId = items[0]?.id;
        if (firstId && barcodeInputsRef.current[firstId]) {
          barcodeInputsRef.current[firstId]?.focus();
          selectAllInput(barcodeInputsRef.current[firstId]);
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen, showBarcode]);

  // Nhận các mặt hàng vừa quét được từ Camera AI / Quét Hóa đơn
  useEffect(() => {
    if (incomingScannedItems && incomingScannedItems.length > 0) {
      const scannedDate = incomingScannedItems[0].transaction_date;
      if (typeof scannedDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(scannedDate)) setTransactionDate(scannedDate);
      setItems((prev) => {
        const cleanPrev = prev.filter((it) => it.name && it.name.trim() !== "");
        const formattedIncoming = incomingScannedItems.map((scanned) => {
          let matchedStock = null;
          if (scanned.stock_id) {
            matchedStock = data_stocks.find((s) => String(s.id) === String(scanned.stock_id));
          }

          return {
            ...scanned,
            ai_reviewed: true,
            stock_selection: scanned.stock_id ? "existing" : "new",
            id: uuidv4(),
            request_id: undefined,
            stock_id: scanned.stock_id || matchedStock?.id || null,
            name: scanned.name || matchedStock?.name || "",
            unit: scanned.unit || matchedStock?.unit || "cái",
            quantity: scanned.quantity == null ? "" : scanned.quantity,
            price: roundPrice(scanned.price),
            retail_price: scanned.retail_price_edited ? roundPrice(scanned.retail_price) : "",
            retail_price_edited: Boolean(scanned.retail_price_edited),
            paid_amount: scanned.paid_amount !== undefined ? roundPrice(scanned.paid_amount) : undefined,
            is_paid_custom: Boolean(scanned.paid_amount !== undefined),
            provider_name: scanned.provider_name || scanned.supplier || "",
            note: scanned.note || scanned.notes || "",
            expiry_date: scanned.expiry_date || "",
            barcode: scanned.barcode || matchedStock?.barcode || "",
          };
        });
        return cleanPrev.length > 0
          ? [...cleanPrev, ...formattedIncoming]
          : formattedIncoming;
      });

      if (onClearIncomingScannedItems) {
        onClearIncomingScannedItems();
      }

      // Tự động focus vào ô đầu tiên của danh sách vừa nạp
      setTimeout(() => {
        const firstEl =
          inputsRef.current[`0-quantity`] ||
          inputsRef.current[`0-barcode`] ||
          inputsRef.current[`0-price`];
        if (firstEl) {
          firstEl.focus();
          selectAllInput(firstEl);
        }
      }, 250);
    }
  }, [incomingScannedItems, onClearIncomingScannedItems]);

  const handleCancel = () => {
    setHasSubmitted(false);
    setItems([emptyItem()]);
    setReceiptCode(formatFallbackReceiptCode(todayDate()));
    toggle();
  };

  // Thêm dòng mới vào bảng
  const handleAddItem = () => {
    const lastItem = items[items.length - 1];
    const newRow = emptyItem();
    if (lastItem && lastItem.provider_name) {
      newRow.provider_name = lastItem.provider_name;
    }
    if (showBarcode && !pendingFocusCellRef.current) {
      pendingFocusRowIdRef.current = newRow.id;
    }
    setItems((prev) => [...prev, newRow]);
  };

  // Xóa một dòng khỏi bảng
  const handleRemoveItem = (id) => {
    setItems((prev) => {
      if (prev.length <= 1) return [emptyItem()];
      return prev.filter((it) => it.id !== id);
    });
  };

  // Lấy giá trị hiển thị cho Creatable Select
  const getSelectedCreatableValue = (item) => {
    if (item.stock_id) {
      const found = data_stocks.find((s) => String(s.id) === String(item.stock_id));
      if (found) {
        return {
          value: found.id,
          label: `${found.name} (${found.unit}) - Tồn: ${roundStockCount(found.count)}`,
          stock: found,
        };
      }
      return { value: item.stock_id, label: item.name || "Mặt hàng" };
    }
    if (item.name && item.name.trim() !== "") {
      return {
        value: item.name,
        label: item.name,
      };
    }
    return null;
  };

  // Xử lý khi chọn từ danh mục hoặc gõ tạo mới mặt hàng tại ô Select
  const handleSelectStockItem = (id, selectedOption) => {
    if (submittingRef.current || retryPending) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        item = { ...item, request_id: undefined, submission_error: undefined };

        if (!selectedOption) {
          // Bấm xóa (clear)
          return {
            ...item,
            stock_id: null,
            stock_selection: "new",
            name: "",
            unit: "cái",
            price: 0,
          };
        }

        // Chọn hàng có sẵn từ kho
        if (selectedOption.stock) {
          const s = selectedOption.stock;
          if (item.ai_reviewed) {
            try { return selectImportStock(item, s, ""); }
            catch (error) { NotificatioErrController(error.message); return item; }
          }
          const rawPrice = s.price_new || s.price || s.avarage_price || 0;
          const rawRetail = (s.retail_price !== undefined && s.retail_price !== null && s.retail_price !== "")
            ? s.retail_price
            : (s.price_sale || s.price || "");
          return {
            ...item,
            stock_id: s.id,
            stock_selection: "existing",
            name: s.name,
            barcode: s.barcode || item.barcode || "",
            unit: s.unit || "cái",
            price: roundPrice(rawPrice),
            retail_price: rawRetail !== "" && Number(rawRetail) > 0 ? roundPrice(rawRetail) : "",
          };
        }

        // Người dùng gõ tên mới và bấm tạo mới (Creatable)
        const newName = (
          selectedOption.label ||
          selectedOption.value ||
          ""
        ).trim();

        return {
          ...item,
          stock_id: null,
          name: newName,
          stock_selection: "new",
        };
      })
    );
  };

  // Xử lý quét mã vạch bằng máy quét hoặc gõ barcode rồi bấm Enter
  const handleBarcodeKeyDown = async (e, currentItem, index) => {
    if (submittingRef.current || retryPending) return;
    if ((currentItem.ai_reviewed || currentItem.stock_selection === "new") && (e.key === "Enter" || e.keyCode === 13)) {
      e.preventDefault();
      return;
    }
    if (e.key === "Enter" || e.keyCode === 13) {
      e.preventDefault();
      const code = (currentItem.barcode || e.target.value || "").trim();
      if (!code) {
        handleKeyDownEnter(e, index, "barcode");
        return;
      }

      // 1. Tìm sản phẩm trong danh mục kho của cửa hàng theo barcode
      const matched = data_stocks.find(
        (s) => s.barcode && String(s.barcode).trim().toLowerCase() === code.toLowerCase()
      );

      if (matched) {
        // Kiểm tra xem sản phẩm này đã có ở dòng khác trong phiếu chưa
        const existingRow = items.find(
          (it) => it.id !== currentItem.id && it.stock_id && String(it.stock_id) === String(matched.id)
        );

        if (existingRow) {
          // Nếu đã có dòng này: tự động tăng số lượng ở dòng đó (+1)
          setItems((prev) =>
            prev.map((it) => {
              if (it.id === existingRow.id) {
                return { ...it, quantity: (Number(it.quantity) || 0) + 1 };
              }
              if (it.id === currentItem.id) {
                return { ...it, barcode: "", stock_id: null, name: "" };
              }
              return it;
            })
          );
          NotificatioSucController(
            `Đã tăng số lượng "${matched.name}" lên ${(Number(existingRow.quantity) || 0) + 1}`
          );
          setTimeout(() => {
            barcodeInputsRef.current[currentItem.id]?.focus();
          }, 50);
          return;
        }

        // Tự nhận diện và gán thông tin sản phẩm, sau đó tự động tạo hàng mới và focus ô barcode hàng mới
        const newRow = emptyItem();
        if (currentItem.provider_name) {
          newRow.provider_name = currentItem.provider_name;
        }
        pendingFocusRowIdRef.current = newRow.id;

        setItems((prev) => {
          const updated = prev.map((it) => {
            if (it.id !== currentItem.id) return it;
            const rawPrice = matched.price_new || matched.price || matched.avarage_price || 0;
            const rawRetail = (matched.retail_price !== undefined && matched.retail_price !== null && matched.retail_price !== "")
              ? matched.retail_price
              : (matched.price_sale || matched.price || "");
            return {
              ...it,
              stock_id: matched.id,
              name: matched.name,
              barcode: matched.barcode || code,
              unit: matched.unit || "cái",
              price: roundPrice(rawPrice),
              retail_price: rawRetail !== "" && Number(rawRetail) > 0 ? roundPrice(rawRetail) : "",
            };
          });
          return [...updated, newRow];
        });
      } else {
        // 2. Không có trong kho cửa hàng -> Tra cứu sản phẩm mẫu (ProductTemplate / Barcode API)
        let tmplItem = null;
        try {
          const token = localStorage.getItem("access_token");
          const res = await axios.get(
            `${END_POINT}/stock/quick-barcode?barcode=${encodeURIComponent(code)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data && res.data.status === 200 && res.data.found && res.data.data) {
            tmplItem = res.data.data;
          }
        } catch (lookupErr) {
          console.warn("Lỗi tra cứu barcode template:", lookupErr);
        }

        if (tmplItem) {
          // Kiểm tra xem sản phẩm mẫu này đã có ở dòng khác trong phiếu chưa (theo barcode hoặc tên)
          const existingRow = items.find(
            (it) => it.id !== currentItem.id && (
              (it.barcode && String(it.barcode).trim().toLowerCase() === code.toLowerCase()) ||
              (it.name && it.name.trim().toLowerCase() === (tmplItem.name || "").trim().toLowerCase())
            )
          );

          if (existingRow) {
            setItems((prev) =>
              prev.map((it) => {
                if (it.id === existingRow.id) {
                  return { ...it, quantity: (Number(it.quantity) || 0) + 1 };
                }
                if (it.id === currentItem.id) {
                  return { ...it, barcode: "", stock_id: null, name: "" };
                }
                return it;
              })
            );
            NotificatioSucController(
              `Đã tăng số lượng "${tmplItem.name}" lên ${(Number(existingRow.quantity) || 0) + 1}`
            );
            setTimeout(() => {
              barcodeInputsRef.current[currentItem.id]?.focus();
            }, 50);
            return;
          }

          // Tự nhận diện và gán thông tin từ sản phẩm mẫu, tự động tạo hàng mới và focus ô barcode hàng mới
          const newRow = emptyItem();
          if (currentItem.provider_name || tmplItem.provider_name) {
            newRow.provider_name = currentItem.provider_name || tmplItem.provider_name;
          }
          pendingFocusRowIdRef.current = newRow.id;

          setItems((prev) => {
            const updated = prev.map((it) => {
              if (it.id !== currentItem.id) return it;
              const rawPrice = tmplItem.price || 0;
              const rawRetail = tmplItem.retail_price || tmplItem.suggested_price || "";
              return {
                ...it,
                stock_id: tmplItem.stock_id || null,
                name: tmplItem.name,
                barcode: tmplItem.barcode || code,
                unit: tmplItem.unit || "cái",
                price: roundPrice(rawPrice),
                retail_price: rawRetail !== "" && Number(rawRetail) > 0 ? roundPrice(rawRetail) : "",
                provider_name: it.provider_name || tmplItem.provider_name || "",
                is_template: true,
              };
            });
            return [...updated, newRow];
          });

          NotificatioSucController(`Tìm thấy sản phẩm mẫu: "${tmplItem.name}"`);
          return;
        }

        // Mã vạch mới chưa có trong kho và chưa có trong mẫu: lưu barcode và tạo hàng mới tiếp theo
        const newRow = emptyItem();
        if (currentItem.provider_name) {
          newRow.provider_name = currentItem.provider_name;
        }
        pendingFocusRowIdRef.current = newRow.id;

        setItems((prev) => {
          const updated = prev.map((it) => {
            if (it.id !== currentItem.id) return it;
            return {
              ...it,
              barcode: code,
              stock_id: null,
            };
          });
          return [...updated, newRow];
        });

        NotificatioSucController(`Đã nhận mã vạch "${code}". Vui lòng chọn hoặc gõ tên mặt hàng.`);
      }
    }
  };

  const handleBarcodeBlur = async (currentItem) => {
    if (submittingRef.current || retryPending || currentItem.ai_reviewed || currentItem.stock_selection === "new") return;
    const code = (currentItem.barcode || "").trim();
    if (!code || currentItem.stock_id) return;

    const matched = data_stocks.find(
      (s) => s.barcode && String(s.barcode).trim().toLowerCase() === code.toLowerCase()
    );

    if (matched) {
      setItems((prev) =>
        prev.map((it) => {
          if (it.id !== currentItem.id) return it;
          const rawPrice = matched.price_new || matched.price || matched.avarage_price || 0;
          const rawRetail = (matched.retail_price !== undefined && matched.retail_price !== null && matched.retail_price !== "")
            ? matched.retail_price
            : (matched.price_sale || matched.price || "");
          return {
            ...it,
            stock_id: matched.id,
            name: it.name || matched.name,
            barcode: matched.barcode || code,
            unit: it.unit || matched.unit || "cái",
            price: roundPrice(rawPrice),
            retail_price: rawRetail !== "" && Number(rawRetail) > 0 ? roundPrice(rawRetail) : it.retail_price,
          };
        })
      );
    } else if (!currentItem.name) {
      // Nếu chưa có tên và không có trong stock: kiểm tra sản phẩm mẫu
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(
          `${END_POINT}/stock/quick-barcode?barcode=${encodeURIComponent(code)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data && res.data.status === 200 && res.data.found && res.data.data) {
          const tmplItem = res.data.data;
          setItems((prev) =>
            prev.map((it) => {
              if (it.id !== currentItem.id) return it;
              const rawCost = tmplItem.price || 0;
              const rawRetail = tmplItem.retail_price || tmplItem.suggested_price || "";
              return {
                ...it,
                stock_id: tmplItem.stock_id || null,
                name: tmplItem.name,
                barcode: tmplItem.barcode || code,
                unit: tmplItem.unit || "cái",
                price: roundPrice(rawCost),
                retail_price: rawRetail !== "" && Number(rawRetail) > 0 ? roundPrice(rawRetail) : it.retail_price,
                is_template: true,
              };
            })
          );
          NotificatioSucController(`Tìm thấy sản phẩm mẫu: "${tmplItem.name}"`);
        }
      } catch (e) {
        // bỏ qua
      }
    }
  };

  // Tính số tiền đã thanh toán của từng mặt hàng
  const getItemPaidAmount = (it) => {
    const lineTotal = Math.round((Number(it.quantity) || 0) * roundPrice(it.price));
    if (!it.provider_name || !it.provider_name.trim()) {
      return lineTotal;
    }
    if (it.is_paid_custom && it.paid_amount !== undefined && it.paid_amount !== "") {
      return Math.min(Math.max(0, Math.round(Number(it.paid_amount) || 0)), lineTotal);
    }
    return lineTotal;
  };

  // Cập nhật giá trị 1 ô trong bảng
  const handleChangeItem = (id, field, value) => {
    if (submittingRef.current || retryPending) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        item = { ...item, request_id: undefined, submission_error: undefined };

        if (field === "stock_id") {
          if (!value) {
            // Nhập hàng mới ngoài danh mục
            return {
              ...item,
              stock_id: null,
              name: "",
              unit: "cái",
              price: 0,
              paid_amount: 0,
              is_paid_custom: false,
            };
          }
          const matched = data_stocks.find((s) => String(s.id) === String(value));
          if (matched) {
            const rawPrice = matched.price || matched.avarage_price || 0;
            const newPrice = roundPrice(rawPrice);
            const newLineTotal = Math.round((Number(item.quantity) || 0) * newPrice);
            let updatedPaid = item.paid_amount;
            if (item.is_paid_custom && typeof updatedPaid === "number" && updatedPaid > newLineTotal) {
              updatedPaid = newLineTotal;
            }
            return {
              ...item,
              stock_id: matched.id,
              name: matched.name,
              unit: matched.unit || "cái",
              price: newPrice,
              paid_amount: updatedPaid,
            };
          }
        }

        if (field === "quantity" || field === "price") {
          const updatedQty = field === "quantity" ? value : item.quantity;
          const updatedPrice = field === "price" ? value : item.price;
          const newLineTotal = Math.round((Number(updatedQty) || 0) * roundPrice(updatedPrice));
          let updatedPaid = item.paid_amount;
          if (item.is_paid_custom && typeof updatedPaid === "number" && updatedPaid > newLineTotal) {
            updatedPaid = newLineTotal;
          }
          return {
            ...item,
            [field]: value,
            paid_amount: updatedPaid,
          };
        }

        if (field === "paid_amount") {
          if (value === "" || value === null || value === undefined) {
            return { ...item, paid_amount: "", is_paid_custom: true };
          }
          const cleanVal = String(value).replace(/[^\d]/g, "");
          if (cleanVal === "") {
            return { ...item, paid_amount: "", is_paid_custom: true };
          }
          const numVal = Number(cleanVal);
          const lineTotal = Math.round((Number(item.quantity) || 0) * roundPrice(item.price));
          const clamped = Math.min(Math.max(0, numVal), lineTotal);
          return { ...item, paid_amount: clamped, is_paid_custom: true };
        }

        if (field === "retail_price") {
          if (value === "" || value === null || value === undefined) {
            return { ...item, [field]: "", retail_price_edited: true };
          }
          return { ...item, [field]: value, retail_price_edited: true };
        }

        if (field === "provider_name") {
          const provider = providers.find((row) => row.name === value);
          const hasProvider = Boolean(value && String(value).trim());
          return {
            ...item,
            [field]: value,
            provider_phone: provider?.phone || "",
            provider_address: provider?.address || "",
            provider_tax_code: provider?.tax_code || "",
            ...(!hasProvider ? { is_paid_custom: false, paid_amount: undefined } : {}),
          };
        }

        return { ...item, [field]: value };
      })
    );
  };

  const handlePaidAmountBlur = (currentItem) => {
    if (submittingRef.current || retryPending) return;
    if (currentItem.paid_amount === "" || currentItem.paid_amount === null || currentItem.paid_amount === undefined) {
      setItems((prev) =>
        prev.map((it) => (it.id === currentItem.id ? { ...it, paid_amount: 0, is_paid_custom: true } : it))
      );
    }
  };

  // Tính tổng số lượng và tổng tiền
  const totalQuantity = items.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0),
    0
  );
  const totalAmount = items.reduce(
    (sum, it) =>
      sum + (Number(it.quantity) || 0) * roundPrice(it.price),
    0
  );

  // Tính tổng đã thanh toán cho toàn bộ phiếu
  const totalPaidAll = items.reduce(
    (sum, it) => sum + getItemPaidAmount(it),
    0
  );
  const totalDebtAll = Math.max(0, totalAmount - totalPaidAll);

  // Gom nhóm các mặt hàng theo nhà cung cấp để tính tiền và công nợ riêng
  const providerGroups = useMemo(() => {
    const valid = items.filter((it) => it.name?.trim());
    const groups = new Map();
    valid.forEach((it) => {
      const pName = (it.provider_name && it.provider_name.trim()) || "";
      if (!pName) return;
      if (!groups.has(pName)) {
        groups.set(pName, {
          name: pName,
          items: [],
          totalAmount: 0,
          paidAmount: 0,
        });
      }
      const grp = groups.get(pName);
      const lineTotal = Math.round((Number(it.quantity) || 0) * roundPrice(it.price));
      const itemPaid = getItemPaidAmount(it);
      grp.items.push(it);
      grp.totalAmount += lineTotal;
      grp.paidAmount += itemPaid;
    });
    return Array.from(groups.values()).map((grp) => ({
      ...grp,
      debtAmount: Math.max(0, grp.totalAmount - grp.paidAmount),
    }));
  }, [items]);

  // Submit toàn bộ phiếu nhập nhiều mặt hàng
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (submittingRef.current) return;

    setHasSubmitted(true);

    if (!transactionDate || !String(transactionDate).trim()) {
      NotificatioErrController("Vui lòng chọn ngày nhập kho");
      if (dateInputRef.current) {
        dateInputRef.current.focus();
      }
      return;
    }

    const allBlank = items.every(isRowBlank);
    let firstErrorFocusFn = null;
    let hasAnyError = false;

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const isAct = allBlank ? i === 0 : !isRowBlank(it);
      if (!isAct) continue;

      const nameErr = !String(it.name || "").trim();
      const unitErr = !String(it.unit || "").trim();
      const qtyErr = !Number.isFinite(Number(it.quantity)) || Number(it.quantity) <= 0;
      const priceErr = !Number.isFinite(Number(it.price)) || roundPrice(it.price) <= 0;

      if (nameErr || unitErr || qtyErr || priceErr) {
        hasAnyError = true;
        if (!firstErrorFocusFn) {
          if (nameErr) {
            firstErrorFocusFn = () => {
              const sel = selectRefs.current[i];
              if (sel && typeof sel.focus === "function") sel.focus();
            };
          } else if (unitErr) {
            firstErrorFocusFn = () => inputsRef.current[`${i}-unit`]?.focus();
          } else if (qtyErr) {
            firstErrorFocusFn = () => inputsRef.current[`${i}-quantity`]?.focus();
          } else if (priceErr) {
            firstErrorFocusFn = () => inputsRef.current[`${i}-price`]?.focus();
          }
        }
      }
    }

    if (hasAnyError) {
      NotificatioErrController("Vui lòng kiểm tra và điền đầy đủ các thông tin được đánh dấu màu đỏ");
      if (firstErrorFocusFn) {
        setTimeout(firstErrorFocusFn, 60);
      }
      return;
    }

    const validItems = items.filter((item) => item.name?.trim());
    if (!validItems.length) { NotificatioErrController("Vui lòng nhập ít nhất 1 mặt hàng"); return; }
    for (let i = 0; i < validItems.length; i++) {
      const error = reviewImportItem(validItems[i]);
      if (error) { NotificatioErrController("Dòng " + (i + 1) + ": " + error); return; }
    }

    const payments = providerGroups.map((grp) => ({
      provider_name: grp.name,
      paid_amount: Math.min(Math.max(0, Math.round(Number(grp.paidAmount) || 0)), grp.totalAmount),
      payment_method: paymentMethod || "cash",
      note: "",
    }));

    const validItemsWithPaid = validItems.map((item) => ({
      ...item,
      paid_amount: getItemPaidAmount(item),
    }));

    const header = importHeaderMetadata(validItemsWithPaid);
    const submission = retryPending ? requestIdRef.current : prepareImportSubmission(validItemsWithPaid, {
      ...header, code: receiptCode.trim(), name: header.provider_name,
      transaction_date: transactionDate,
      payments,
      paid_amount: totalPaidAll,
      payment_method: paymentMethod || "cash",
    }, requestIdRef.current, uuidv4);
    requestIdRef.current = submission;
    const submittedItems = validItemsWithPaid.map((item, index) => ({ ...item, request_id: submission.payload.items[index].request_id }));
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError("");
    setItems(submittedItems);
    try {
      const res = await axios.post(END_POINT + "/warehouse-import/batch", submission.payload, {
        headers: { Authorization: "Bearer " + localStorage.getItem("access_token") }, timeout: 120000,
      });
      if (![200, 202].includes(res.data?.status || res.status))
        throw Object.assign(new Error(res.data?.responseText || "Không thể lưu phiếu"), { response: res });
      const remaining = remainingImportItems(submittedItems, res.data?.data);
      if (remaining) {
        setItems(remaining.items);
        setRetryPending(remaining.uncertain);
        if (!remaining.uncertain) requestIdRef.current = null;
        setSubmitError(remaining.uncertain ? "Chưa xác định dòng đã lưu. Gửi lại cùng phiếu để kiểm tra."
          : "Các dòng đã lưu đã được bỏ khỏi biểu mẫu; hãy sửa và gửi lại các dòng còn lỗi.");
        if (onSuccess) onSuccess();
        return;
      }
      NotificatioSucController(res.data?.responseText || "Đã gửi phiếu nhập thành công");
      requestIdRef.current = null;
      setRetryPending(false);
      setHasSubmitted(false);
      setItems([emptyItem()]);
      setReceiptCode(formatFallbackReceiptCode(todayDate()));
      toggle();
      if (onSuccess) onSuccess();
    } catch (error) {
      const uncertain = !error.response || error.response.status >= 500;
      setRetryPending(uncertain);
      const message = uncertain ? "Chưa xác định kết quả lưu. Gửi lại cùng phiếu; mã yêu cầu được giữ nguyên."
        : error.response?.data?.responseText || error.message;
      setSubmitError(message);
      NotificatioErrController(message);
    } finally { submittingRef.current = false; setIsSubmitting(false); }
  };

  const allRowsBlank = items.every(isRowBlank);
  const dateError = hasSubmitted && (!transactionDate || !String(transactionDate).trim());
  const hasValidationErrors =
    dateError ||
    items.some((item, index) => {
      const isAct = allRowsBlank ? index === 0 : !isRowBlank(item);
      if (!isAct) return false;
      return (
        !String(item.name || "").trim() ||
        !String(item.unit || "").trim() ||
        !Number.isFinite(Number(item.quantity)) ||
        Number(item.quantity) <= 0 ||
        !Number.isFinite(Number(item.price)) ||
        roundPrice(item.price) <= 0
      );
    });

  return (
    <Modal
      isOpen={isOpen}
      toggle={editsDisabled ? undefined : toggle}
      size="xl"
      className="multi-import-modal"
      backdrop="static"
    >
      <div className="multi-import-header">
        <div className="d-flex align-items-center gap-2">
          <h5>
            <i className="simple-icon-basket-loaded mr-2" />
            {title || "Tạo Phiếu Nhập Kho"}
          </h5>
          <span
            className="header-badge ml-3"
            style={{
              background: "rgba(59, 130, 246, 0.25)",
              color: "#93c5fd",
              border: "1px solid rgba(147, 197, 253, 0.3)",
              fontSize: "0.85rem",
              padding: "4px 10px",
            }}
          >
            <i className="simple-icon-tag mr-1" /> Mã: {receiptCode}
          </span>
        </div>

        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center mr-3">
            <span className="text-white-50 small mr-2 font-weight-bold">
              <i className="simple-icon-calendar mr-1" /> NGÀY NHẬP:
            </span>
            <div className="d-flex flex-column">
              <input
                ref={dateInputRef}
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                onFocus={handleInputFocus}
                onClick={handleInputClick}
                disabled={editsDisabled}
                className="form-control form-control-sm"
                style={{
                  width: "145px",
                  height: "34px",
                  background: dateError ? "rgba(239, 68, 68, 0.25)" : "rgba(255, 255, 255, 0.15)",
                  color: "#ffffff",
                  border: dateError ? "1px solid #ef4444" : "1px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: dateError ? "0 0 0 2px rgba(239, 68, 68, 0.5)" : undefined,
                  borderRadius: "6px",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              />
              {dateError && (
                <span style={{ color: "#fca5a5", fontSize: "0.72rem", fontWeight: 600, marginTop: "2px" }}>
                  Vui lòng chọn ngày nhập
                </span>
              )}
            </div>
          </div>

          {onOpenScanner && (
            <button
              type="button"
              className="btn-scan-trigger-pill mr-3"
              onClick={onOpenScanner}
              disabled={editsDisabled}
              title="Quét Camera AI / Barcode để tự động điền danh sách mặt hàng"
            >
              <i className="simple-icon-camera" /> Quét Camera AI / Hóa đơn
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-light btn-sm"
            onClick={handleCancel}
            disabled={editsDisabled}
          >
            <i className="simple-icon-close" />
          </button>
        </div>
      </div>

      <ModalBody style={{ padding: "16px 20px" }}>
        {submitError && <div className="alert alert-warning" role="alert">{submitError}</div>}
        {hasSubmitted && hasValidationErrors && (
          <div
            className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center"
            style={{ fontSize: "0.85rem", borderRadius: "8px" }}
          >
            <i className="simple-icon-exclamation mr-2" style={{ fontSize: "1.1rem" }} />
            <span>Vui lòng kiểm tra và điền đầy đủ các thông tin được đánh dấu màu đỏ bên dưới.</span>
          </div>
        )}
        {items.filter((item) => item.submission_error).map((item) =>
          <div className="text-danger mb-2" key={item.id}>{item.name}: {item.submission_error}</div>)}
        <fieldset disabled={editsDisabled} style={{ border: 0, padding: 0, minWidth: 0 }}>
        {/* Bảng danh sách mặt hàng */}
        <div className="multi-import-card" style={{ padding: 0, margin: 0 }}>
          <div style={{ padding: "14px 18px 8px 18px" }}>
            <div className="multi-import-card-title mb-1">
              <span>
                <i className="simple-icon-list mr-1" /> Danh sách mặt hàng nhập kho (
                {items.length} dòng)
              </span>
              <span className="text-muted small">
                Chọn hoặc gõ tạo mặt hàng mới • Chọn nhà cung cấp và ghi chú riêng cho từng dòng
              </span>
            </div>
          </div>

          <div
            className="multi-import-table-container mb-0"
            style={{ border: "none", overflowX: "auto" }}
          >
            <Table responsive className="multi-import-table mb-0" style={{ minWidth: showBarcode ? "1500px" : "1300px" }}>
              <thead>
                <tr>
                  <th style={{ width: "40px" }} className="text-center">#</th>
                  {showBarcode && (
                    <th style={{ width: "140px" }}>
                      <i className="simple-icon-cursor mr-1" /> Barcode
                    </th>
                  )}
                  <th style={{ minWidth: showBarcode ? "240px" : "280px" }}>Mặt hàng</th>
                  <th style={{ width: "85px" }}>ĐVT</th>
                  <th style={{ width: "90px" }}>Số lượng</th>
                  <th style={{ width: "115px" }} className="text-right">Đơn giá (đ)</th>
                  <th style={{ width: "120px" }} className="text-right">Thành tiền</th>
                  {showBarcode && (
                    <th style={{ width: "115px" }} className="text-right">Giá bán lẻ (đ)</th>
                  )}
                  <th style={{ width: "220px" }}>
                    <i className="simple-icon-people mr-1" /> Nhà cung cấp
                  </th>
                  <th style={{ width: "135px" }} className="text-right">
                    Đã thanh toán (đ)
                  </th>
                  <th style={{ width: "150px" }}>
                    <i className="simple-icon-note mr-1" /> Ghi chú
                  </th>
                  <th style={{ width: "125px" }}>Hạn dùng</th>
                  <th style={{ width: "45px" }} className="text-center"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const lineTotal =
                    (Number(item.quantity) || 0) * roundPrice(item.price);

                  const isRowAct = allRowsBlank ? index === 0 : !isRowBlank(item);
                  const nameError = hasSubmitted && isRowAct && !String(item.name || "").trim() ? "Vui lòng chọn hoặc nhập tên mặt hàng" : "";
                  const unitError = hasSubmitted && isRowAct && !String(item.unit || "").trim() ? "Nhập ĐVT" : "";
                  const qtyError = hasSubmitted && isRowAct && (!Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0) ? "Số lượng phải > 0" : "";
                  const priceError = hasSubmitted && isRowAct && (!Number.isFinite(Number(item.price)) || roundPrice(item.price) <= 0) ? "Đơn giá phải > 0" : "";

                  // Các mặt hàng đã được chọn ở các dòng khác (để không bị chọn trùng lặp)
                  const otherSelectedStockIds = new Set(
                    items
                      .filter((it) => it.id !== item.id && it.stock_id)
                      .map((it) => String(it.stock_id))
                  );
                  const availableStocks = data_stocks.filter(
                    (s) =>
                      !otherSelectedStockIds.has(String(s.id)) ||
                      String(s.id) === String(item.stock_id)
                  );

                  return (
                    <tr key={item.id}>
                      <td className="line-number" style={{ paddingTop: "17px" }}>{index + 1}</td>
                      {showBarcode && (
                        <td style={{ width: "140px" }}>
                          <input
                            type="text"
                            ref={(el) => {
                              if (el) {
                                barcodeInputsRef.current[item.id] = el;
                                inputsRef.current[`${index}-barcode`] = el;
                              } else {
                                delete barcodeInputsRef.current[item.id];
                                delete inputsRef.current[`${index}-barcode`];
                              }
                            }}
                            placeholder="Quét mã vạch..."
                            value={item.barcode || ""}
                            onFocus={handleInputFocus}
                            onClick={handleInputClick}
                            onChange={(e) =>
                              handleChangeItem(item.id, "barcode", e.target.value)
                            }
                            onKeyDown={(e) => handleBarcodeKeyDown(e, item, index)}
                            onBlur={() => handleBarcodeBlur(item)}
                            disabled={editsDisabled}
                            className="form-control"
                            style={{
                              fontSize: "0.85rem",
                              height: "38px",
                              borderRadius: "6px",
                              fontFamily: "monospace",
                            }}
                          />
                        </td>
                      )}
                      <td style={{ minWidth: showBarcode ? "240px" : "280px" }}>
                        <Creatable
                          ref={(el) => {
                            if (el) selectRefs.current[index] = el;
                            else delete selectRefs.current[index];
                          }}
                          className={`react-select-sm w-100 ${nameError ? "is-invalid-custom" : ""}`}
                          classNamePrefix="react-select"
                          placeholder="Chọn hoặc tạo mới..."
                          options={availableStocks.map((s) => ({
                            value: s.id,
                            label: `${s.name} (${s.unit}) - Tồn: ${roundStockCount(s.count)}`,
                            stock: s,
                          }))}
                          value={getSelectedCreatableValue(item)}
                          onChange={(opt) => handleSelectStockItem(item.id, opt)}
                          isDisabled={editsDisabled}
                          isClearable
                          formatCreateLabel={(inputValue) => `+ Tạo mới hàng: "${inputValue}"`}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            container: (base) => ({ ...base, width: "100%" }),
                            control: (base) => ({
                              ...base,
                              minHeight: 38,
                              height: 38,
                              borderColor: nameError ? "#ef4444 !important" : "#cbd5e1",
                              boxShadow: nameError ? "0 0 0 1px #ef4444" : undefined,
                              backgroundColor: nameError ? "#fef2f2" : undefined,
                              borderRadius: 6,
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 8px",
                              flexWrap: "nowrap",
                              overflow: "hidden",
                            }),
                            placeholder: (base) => ({
                              ...base,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              wordBreak: "keep-all",
                            }),
                          }}
                        />
                        {nameError && (
                          <span className="multi-import-error-feedback">
                            {nameError}
                          </span>
                        )}
                      </td>

                      <td style={{ width: "85px" }}>
                        <Input
                          type="text"
                          innerRef={(el) => {
                            if (el) inputsRef.current[`${index}-unit`] = el;
                            else delete inputsRef.current[`${index}-unit`];
                          }}
                          value={item.unit}
                          placeholder="cái, kg..."
                          onFocus={handleInputFocus}
                          onClick={handleInputClick}
                          onKeyDown={(e) => handleKeyDownEnter(e, index, "unit")}
                          onChange={(e) =>
                            handleChangeItem(item.id, "unit", e.target.value)
                          }
                          disabled={editsDisabled}
                          className={unitError ? "is-invalid-custom" : ""}
                        />
                        {unitError && (
                          <span className="multi-import-error-feedback">
                            {unitError}
                          </span>
                        )}
                      </td>

                      <td style={{ width: "90px" }}>
                        <Input
                          type="number"
                          innerRef={(el) => {
                            if (el) inputsRef.current[`${index}-quantity`] = el;
                            else delete inputsRef.current[`${index}-quantity`];
                          }}
                          min="0.001"
                          step="0.001"
                          value={item.quantity}
                          onFocus={handleInputFocus}
                          onClick={handleInputClick}
                          onKeyDown={(e) => handleKeyDownEnter(e, index, "quantity")}
                          onChange={(e) =>
                            handleChangeItem(item.id, "quantity", e.target.value)
                          }
                          disabled={editsDisabled}
                          className={qtyError ? "is-invalid-custom" : ""}
                        />
                        {qtyError && (
                          <span className="multi-import-error-feedback">
                            {qtyError}
                          </span>
                        )}
                      </td>

                      <td style={{ width: "115px" }}>
                        <Input
                          type="text"
                          inputMode="numeric"
                          innerRef={(el) => {
                            if (el) inputsRef.current[`${index}-price`] = el;
                            else delete inputsRef.current[`${index}-price`];
                          }}
                          value={formatNumberWithDot(item.price)}
                          placeholder="0"
                          onFocus={handleInputFocus}
                          onClick={handleInputClick}
                          onKeyDown={(e) => handleKeyDownEnter(e, index, "price")}
                          onChange={(e) =>
                            handleChangeItem(item.id, "price", e.target.value)
                          }
                          disabled={editsDisabled}
                          className={priceError ? "is-invalid-custom" : ""}
                          style={{ textAlign: "right" }}
                        />
                        {priceError && (
                          <span className="multi-import-error-feedback" style={{ textAlign: "right" }}>
                            {priceError}
                          </span>
                        )}
                      </td>

                      <td className="line-total text-right" style={{ width: "120px", paddingTop: "17px" }}>
                        {formatNumberWithDot(Math.round(lineTotal)) || "0"} đ
                      </td>

                      {showBarcode && (
                        <td style={{ width: "115px" }}>
                          <Input
                            type="text"
                            inputMode="numeric"
                            innerRef={(el) => {
                              if (el) inputsRef.current[`${index}-retail_price`] = el;
                              else delete inputsRef.current[`${index}-retail_price`];
                            }}
                            placeholder={item.price ? formatNumberWithDot(item.price) : "Bằng giá nhập"}
                            value={formatNumberWithDot(item.retail_price)}
                            onFocus={handleInputFocus}
                            onClick={handleInputClick}
                            onKeyDown={(e) => handleKeyDownEnter(e, index, "retail_price")}
                            onChange={(e) =>
                              handleChangeItem(item.id, "retail_price", e.target.value)
                            }
                            disabled={editsDisabled}
                            style={{ textAlign: "right" }}
                          />
                        </td>
                      )}

                      {/* Select Nhà cung cấp cho từng dòng */}
                      <td style={{ width: "220px" }}>
                        <Creatable
                          className="react-select-sm w-100"
                          classNamePrefix="react-select"
                          placeholder="Chọn/nhập NCC..."
                          options={providers.map((p) => ({
                            value: p.name,
                            label: p.name,
                          }))}
                          value={
                            item.provider_name
                              ? { value: item.provider_name, label: item.provider_name }
                              : null
                          }
                          onChange={(opt) =>
                            handleChangeItem(
                              item.id,
                              "provider_name",
                              opt ? opt.value : ""
                            )
                          }
                          isDisabled={editsDisabled}
                          isClearable
                          formatCreateLabel={(inputValue) => `+ NCC mới: "${inputValue}"`}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            container: (base) => ({ ...base, width: "100%" }),
                            control: (base) => ({
                              ...base,
                              minHeight: 38,
                              height: 38,
                              borderColor: "#cbd5e1",
                              borderRadius: 6,
                              fontSize: "0.85rem",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 8px",
                            }),
                          }}
                        />
                      </td>

                      {/* Cột Đã thanh toán */}
                      <td style={{ width: "135px" }}>
                        <Input
                          type="text"
                          inputMode="numeric"
                          innerRef={(el) => {
                            if (el) inputsRef.current[`${index}-paid_amount`] = el;
                            else delete inputsRef.current[`${index}-paid_amount`];
                          }}
                          value={
                            item.paid_amount === ""
                              ? ""
                              : formatNumberWithDot(
                                  !item.provider_name?.trim()
                                    ? Math.round(lineTotal)
                                    : item.is_paid_custom && item.paid_amount !== undefined
                                    ? item.paid_amount
                                    : Math.round(lineTotal)
                                )
                          }
                          placeholder="0"
                          onFocus={handleInputFocus}
                          onClick={handleInputClick}
                          onKeyDown={(e) => handleKeyDownEnter(e, index, "paid_amount")}
                          onChange={(e) =>
                            handleChangeItem(item.id, "paid_amount", e.target.value)
                          }
                          onBlur={() => handlePaidAmountBlur(item)}
                          disabled={editsDisabled || !item.provider_name?.trim()}
                          title={
                            !item.provider_name?.trim()
                              ? "Cần chọn Nhà cung cấp để điều chỉnh thanh toán / ghi nhận công nợ"
                              : `Tối đa ${formatNumberWithDot(Math.round(lineTotal))} đ`
                          }
                          style={{
                            textAlign: "right",
                            fontWeight: 600,
                            color:
                              item.provider_name?.trim() &&
                              item.is_paid_custom &&
                              (Number(item.paid_amount) || 0) < Math.round(lineTotal)
                                ? "#ef4444"
                                : "#10b981",
                            backgroundColor: !item.provider_name?.trim() ? "#f1f5f9" : undefined,
                            cursor: !item.provider_name?.trim() ? "not-allowed" : undefined,
                          }}
                        />
                      </td>

                      {/* Ghi chú riêng cho từng dòng */}
                      <td style={{ width: "150px" }}>
                        <Input
                          type="text"
                          innerRef={(el) => {
                            if (el) inputsRef.current[`${index}-note`] = el;
                            else delete inputsRef.current[`${index}-note`];
                          }}
                          placeholder="Ghi chú dòng..."
                          value={item.note || ""}
                          onFocus={handleInputFocus}
                          onClick={handleInputClick}
                          onKeyDown={(e) => handleKeyDownEnter(e, index, "note")}
                          onChange={(e) =>
                            handleChangeItem(item.id, "note", e.target.value)
                          }
                          disabled={editsDisabled}
                          style={{
                            fontSize: "0.85rem",
                            height: "38px",
                            borderRadius: "6px",
                          }}
                        />
                      </td>

                      <td style={{ width: "125px" }}>
                        <Input
                          type="date"
                          innerRef={(el) => {
                            if (el) inputsRef.current[`${index}-expiry_date`] = el;
                            else delete inputsRef.current[`${index}-expiry_date`];
                          }}
                          value={item.expiry_date}
                          onFocus={handleInputFocus}
                          onClick={handleInputClick}
                          onKeyDown={(e) => handleKeyDownEnter(e, index, "expiry_date")}
                          onChange={(e) =>
                            handleChangeItem(item.id, "expiry_date", e.target.value)
                          }
                          disabled={editsDisabled}
                        />
                      </td>

                      <td className="text-center" style={{ width: "45px", paddingTop: "12px" }}>
                        <button
                          type="button"
                          className="delete-row-btn"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={editsDisabled || items.length === 1}
                          title="Xóa dòng"
                        >
                          <i className="simple-icon-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <div className="multi-import-actions-bar">
              <div className="d-flex align-items-center gap-2">
                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={handleAddItem}
                  disabled={editsDisabled}
                  className="font-weight-bold"
                >
                  <i className="simple-icon-plus mr-1" /> Thêm dòng mặt hàng
                </Button>
                {totalPaidAll > 0 && (
                  <div className="d-flex align-items-center ml-3">
                    <span className="text-muted small font-weight-bold mr-2 text-nowrap">
                      Hình thức chi:
                    </span>
                    <select
                      className="form-control form-control-sm"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled={editsDisabled}
                      style={{
                        width: "130px",
                        height: "32px",
                        padding: "0 8px",
                        lineHeight: "30px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        boxSizing: "border-box",
                        verticalAlign: "middle",
                      }}
                    >
                      <option value="cash">Tiền mặt</option>
                      <option value="bank">Chuyển khoản</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="multi-import-summary-box">
                <div className="multi-import-summary-item">
                  <span className="multi-import-summary-label">Tổng số lượng</span>
                  <span className="multi-import-summary-val" style={{ color: "#3b82f6" }}>
                    {roundStockCount(totalQuantity)}
                  </span>
                </div>
                <div
                  style={{
                    height: "36px",
                    width: "1px",
                    background: "#cbd5e1",
                  }}
                />
                <div className="multi-import-summary-item">
                  <span className="multi-import-summary-label">Tổng tiền hàng</span>
                  <span className="multi-import-summary-val">
                    {formatNumberWithDot(totalAmount) || "0"} đ
                  </span>
                </div>
                <div
                  style={{
                    height: "36px",
                    width: "1px",
                    background: "#cbd5e1",
                  }}
                />
                <div className="multi-import-summary-item">
                  <span className="multi-import-summary-label">Đã thanh toán</span>
                  <span className="multi-import-summary-val" style={{ color: "#10b981" }}>
                    {formatNumberWithDot(totalPaidAll) || "0"} đ
                  </span>
                </div>
                <div
                  style={{
                    height: "36px",
                    width: "1px",
                    background: "#cbd5e1",
                  }}
                />
                <div className="multi-import-summary-item">
                  <span className="multi-import-summary-label">Công nợ còn lại</span>
                  <span
                    className="multi-import-summary-val"
                    style={{ color: totalDebtAll > 0 ? "#ef4444" : "#10b981" }}
                  >
                    {formatNumberWithDot(totalDebtAll) || "0"} đ
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </fieldset>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <Button
          color="secondary"
          outline
          onClick={handleCancel}
          disabled={editsDisabled}
        >
          Hủy bỏ
        </Button>
        <div className="d-flex gap-2">
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || items.length === 0}
            className="font-weight-bold px-4"
          >
            {isSubmitting ? (
              <span>
                <i className="simple-icon-refresh spin mr-2" /> Đang lưu kho...
              </span>
            ) : (
              <span>
                <i className="simple-icon-check mr-2" /> {retryPending ? "Gửi lại cùng phiếu" : "Lưu Phiếu Nhập"} (
                {formatNumberWithDot(totalAmount) || "0"} đ{totalDebtAll > 0 ? ` | Nợ: ${formatNumberWithDot(totalDebtAll)} đ` : ""})
              </span>
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(AddWarehouseImport);
